/**
 * StoresSearch - Explore and search stores page
 * Route: /tiendas
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, Store, Star, Truck, Package, MapPin, Filter, X,
  ChevronDown, ChevronLeft, ChevronRight, SlidersHorizontal,
  Clock, BadgeCheck, LayoutGrid, List
} from 'lucide-react';
import { searchStores, getMarketplaceBusinessTypes } from '../../services/api';
import { VerifiedStoreName } from '../../components/marketplace/VerifiedBadge';
import { MarketplaceHeader, MarketplaceFooter, FloatingCartButton, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';

const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
  lightGray: '#f8fafc',
  white: '#ffffff'
};

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevancia' },
  { value: 'rating', label: 'Mejor valoradas' },
  { value: 'newest', label: 'Mas recientes' },
  { value: 'name', label: 'Nombre A-Z' },
  { value: 'distance', label: 'Mas cercanas' },
];

const StoresSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  // Filter state from URL
  const query = searchParams.get('q') || '';
  const selectedType = searchParams.get('type') || '';
  const sortBy = searchParams.get('sort') || 'relevance';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const verifiedOnly = searchParams.get('verified') === 'true';
  const deliveryEnabled = searchParams.get('delivery') === 'true' || null;
  const pickupEnabled = searchParams.get('pickup') === 'true' || null;
  const openNow = searchParams.get('open') === 'true';

  // Count active filters
  const activeFilterCount = [verifiedOnly, deliveryEnabled, pickupEnabled, openNow, selectedType].filter(Boolean).length;

  // Get user geolocation
  useEffect(() => {
    const cached = localStorage.getItem('user_geolocation');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          setUserLocation(parsed);
          return;
        }
      } catch {}
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, timestamp: Date.now() };
          localStorage.setItem('user_geolocation', JSON.stringify(loc));
          setUserLocation(loc);
        },
        () => {} // silently fail
      );
    }
  }, []);

  // Load business types
  useEffect(() => {
    getMarketplaceBusinessTypes()
      .then(res => setBusinessTypes(res.data || []))
      .catch(() => {});
  }, []);

  // Load stores
  const loadStores = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort_by: sortBy };
      if (query) params.q = query;
      if (selectedType) params.business_type = selectedType;
      if (verifiedOnly) params.verified_only = true;
      if (deliveryEnabled) params.delivery_enabled = true;
      if (pickupEnabled) params.pickup_enabled = true;
      if (openNow) params.open_now = true;
      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
      }
      const res = await searchStores(params);
      setStores(res.data.stores || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 0);
    } catch (e) {
      console.error('Error searching stores:', e);
    } finally {
      setLoading(false);
    }
  }, [query, selectedType, sortBy, page, verifiedOnly, deliveryEnabled, pickupEnabled, openNow, userLocation]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  // URL param helpers
  const updateParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page'); // reset page on filter change
    setSearchParams(params);
  };

  const toggleParam = (key) => {
    const current = searchParams.get(key) === 'true';
    updateParam(key, current ? '' : 'true');
  };

  const setPage = (p) => {
    const params = new URLSearchParams(searchParams);
    if (p > 1) params.set('page', p.toString());
    else params.delete('page');
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchParams(query ? { q: query } : {});
  };

  // Search form
  const [searchInput, setSearchInput] = useState(query);
  const handleSearch = (e) => {
    e.preventDefault();
    updateParam('q', searchInput.trim());
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <MarketplaceHeader />

      {/* Hero Section */}
      <section
        className="relative py-12 md:py-16"
        style={{ background: `linear-gradient(135deg, ${COLORS.navyBlue} 0%, ${COLORS.darkSlate} 100%)` }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-10 right-20 w-48 h-48 rounded-full bg-orange-500/20 blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white/80 text-sm mb-4">
            <Store className="w-4 h-4" />
            <span>Explora comercios locales</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Descubre Tiendas Locales
          </h1>
          <p className="text-white/70 mb-6 text-sm md:text-base">
            Encuentra los mejores negocios cerca de ti. Compra local, apoya a tu comunidad.
          </p>
          <form onSubmit={handleSearch} className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar tiendas por nombre, tipo..."
                className="w-full pl-12 pr-24 py-3.5 rounded-full border-0 shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-sm md:text-base"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 rounded-full text-white font-medium text-sm transition-colors hover:opacity-90"
                style={{ backgroundColor: COLORS.orange }}
              >
                Buscar
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Filters Bar */}
      <div className="sticky top-16 z-30 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                showFilters || activeFilterCount > 0 ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtros
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center" style={{ backgroundColor: COLORS.orange }}>
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Category chips - horizontal scroll */}
            <div className="flex-1 overflow-x-auto scrollbar-hide">
              <div className="flex gap-2">
                <button
                  onClick={() => updateParam('type', '')}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    !selectedType ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  style={!selectedType ? { backgroundColor: COLORS.navyBlue } : {}}
                >
                  Todas
                </button>
                {businessTypes.map(bt => (
                  <button
                    key={bt.id || bt.name}
                    onClick={() => updateParam('type', selectedType === bt.name ? '' : bt.name)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      selectedType === bt.name ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    style={selectedType === bt.name ? { backgroundColor: COLORS.navyBlue } : {}}
                  >
                    {bt.icon && <span className="mr-1">{bt.icon}</span>}
                    {bt.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort dropdown */}
            <div className="relative flex-shrink-0">
              <select
                value={sortBy}
                onChange={(e) => updateParam('sort', e.target.value === 'relevance' ? '' : e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500/30"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>

            {/* Results count */}
            <div className="hidden sm:block text-sm text-slate-500 flex-shrink-0">
              {!loading && <span>{total} tienda{total !== 1 ? 's' : ''}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel + Results */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex-1 w-full">
        <div className="flex gap-6">
          {/* Sidebar Filters - Desktop */}
          {showFilters && (
            <div className="hidden md:block w-64 flex-shrink-0">
              <div className="bg-white rounded-xl border p-5 sticky top-36">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Filtros</h3>
                  {activeFilterCount > 0 && (
                    <button onClick={clearFilters} className="text-xs text-orange-600 hover:underline">
                      Limpiar
                    </button>
                  )}
                </div>

                <div className="space-y-5">
                  {/* Open now */}
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={openNow}
                      onChange={() => toggleParam('open')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">Abierto ahora</span>
                    </div>
                  </label>

                  {/* Verified */}
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={verifiedOnly}
                      onChange={() => toggleParam('verified')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">Verificadas</span>
                    </div>
                  </label>

                  <hr className="border-slate-100" />

                  {/* Delivery */}
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={!!deliveryEnabled}
                      onChange={() => toggleParam('delivery')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">Envio a domicilio</span>
                    </div>
                  </label>

                  {/* Pickup */}
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={!!pickupEnabled}
                      onChange={() => toggleParam('pickup')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-700 group-hover:text-slate-900">Recoger en tienda</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Filter Modal */}
          {showFilters && (
            <div className="md:hidden fixed inset-0 z-50 flex">
              <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
              <div className="relative ml-auto w-80 max-w-[85vw] bg-white h-full overflow-y-auto animate-slide-in-right">
                <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Filtros</h3>
                  <button onClick={() => setShowFilters(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-5 space-y-5">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={openNow}
                      onChange={() => toggleParam('open')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Abierto ahora</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={verifiedOnly}
                      onChange={() => toggleParam('verified')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="w-4 h-4 text-blue-500" />
                      <span className="text-sm">Verificadas</span>
                    </div>
                  </label>
                  <hr />
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!deliveryEnabled}
                      onChange={() => toggleParam('delivery')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">Envio a domicilio</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!pickupEnabled}
                      onChange={() => toggleParam('pickup')}
                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <div className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">Recoger en tienda</span>
                    </div>
                  </label>
                  <hr />
                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => { clearFilters(); setShowFilters(false); }}
                      className="w-full py-2.5 text-sm font-medium text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50"
                    >
                      Limpiar filtros
                    </button>
                  )}
                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-full py-2.5 text-sm font-medium text-white rounded-lg"
                    style={{ backgroundColor: COLORS.navyBlue }}
                  >
                    Ver resultados ({total})
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results Grid */}
          <div className="flex-1 min-w-0">
            {/* Active filters tags */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedType && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                    {selectedType}
                    <button onClick={() => updateParam('type', '')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {verifiedOnly && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                    Verificadas
                    <button onClick={() => toggleParam('verified')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {deliveryEnabled && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                    Envio
                    <button onClick={() => toggleParam('delivery')} className="hover:text-green-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {pickupEnabled && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-medium">
                    Recoger
                    <button onClick={() => toggleParam('pickup')} className="hover:text-orange-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {openNow && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                    Abierto ahora
                    <button onClick={() => toggleParam('open')} className="hover:text-green-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline">
                  Limpiar todo
                </button>
              </div>
            )}

            {/* Mobile result count */}
            <div className="sm:hidden text-sm text-slate-500 mb-3">
              {!loading && <span>{total} tienda{total !== 1 ? 's' : ''} encontrada{total !== 1 ? 's' : ''}</span>}
            </div>

            {/* Loading Skeleton */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-16 h-16 rounded-xl bg-slate-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-200 rounded w-3/4" />
                        <div className="h-3 bg-slate-100 rounded w-1/2" />
                      </div>
                    </div>
                    <div className="h-3 bg-slate-100 rounded w-full mb-2" />
                    <div className="flex gap-2">
                      <div className="h-6 bg-slate-100 rounded-full w-16" />
                      <div className="h-6 bg-slate-100 rounded-full w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : stores.length === 0 ? (
              /* Empty State */
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                  <Store className="w-10 h-10 text-slate-300" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No se encontraron tiendas</h3>
                <p className="text-slate-500 mb-6 text-sm max-w-md mx-auto">
                  {query
                    ? `No hay tiendas que coincidan con "${query}". Intenta con otros terminos.`
                    : 'No hay tiendas que coincidan con los filtros seleccionados.'}
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="px-5 py-2.5 rounded-full text-white font-medium text-sm"
                    style={{ backgroundColor: COLORS.navyBlue }}
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            ) : (
              /* Store Cards Grid */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {stores.map(store => (
                  <StoreCard key={store.business_id} store={store} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                  let pageNum;
                  if (pages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= pages - 3) {
                    pageNum = pages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                        pageNum === page
                          ? 'text-white'
                          : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                      style={pageNum === page ? { backgroundColor: COLORS.navyBlue } : {}}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pages}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <MarketplaceFooter />
      <FloatingCartButton />
      <MobileBottomNav />
    </div>
  );
};

// Store Card Component
const StoreCard = ({ store }) => {
  const isOpen = store.open_status?.is_open;
  const openMessage = store.open_status?.message || '';

  return (
    <Link
      to={`/tienda/${store.business_id}`}
      className="bg-white rounded-xl border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all p-4 flex flex-col"
    >
      {/* Header: Logo + Info */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-16 h-16 rounded-xl flex-shrink-0 bg-slate-100 overflow-hidden flex items-center justify-center">
          {store.logo_thumbnail_url || store.logo_url ? (
            <img
              src={store.logo_thumbnail_url || store.logo_url}
              alt={store.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Store className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <VerifiedStoreName
            name={store.name}
            verified={store.is_verified}
            size="sm"
            nameClassName="font-semibold text-sm truncate block text-slate-900"
          />
          {store.category && (
            <p className="text-xs text-slate-500 truncate mt-0.5">{store.category}</p>
          )}
          {/* Open status */}
          <div className="mt-1">
            {isOpen ? (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Abierto
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                {openMessage || 'Cerrado'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Address */}
      {store.branch_address && (
        <p className="text-xs text-slate-500 mb-3 flex items-start gap-1.5 line-clamp-2">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-slate-400" />
          {store.branch_address}
        </p>
      )}

      {/* Stats Row */}
      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3 flex-wrap">
        {store.rating > 0 && (
          <span className="flex items-center gap-1">
            <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
            <span className="font-medium text-slate-700">{store.rating}</span>
            <span className="text-slate-400">({store.review_count})</span>
          </span>
        )}
        {store.product_count > 0 && (
          <span className="flex items-center gap-1">
            <Package className="w-3.5 h-3.5 text-slate-400" />
            {store.product_count} producto{store.product_count !== 1 ? 's' : ''}
          </span>
        )}
        {store.distance_km != null && (
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-slate-400" />
            {store.distance_km < 1 ? `${Math.round(store.distance_km * 1000)}m` : `${store.distance_km} km`}
          </span>
        )}
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 mt-auto flex-wrap">
        {store.delivery_enabled && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-xs font-medium" style={{ color: COLORS.navyBlue }}>
            <Truck className="w-3 h-3" /> Envio
          </span>
        )}
        {store.pickup_enabled && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-xs font-medium" style={{ color: COLORS.orange }}>
            <Store className="w-3 h-3" /> Recoger
          </span>
        )}
      </div>
    </Link>
  );
};

export default StoresSearch;
